import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isCreditRefusal, CHROME_STORE_URL } from "@veyso/core";
import { AppShell } from "../components/AppShell.js";
import { ProjectNav } from "../components/ProjectNav.js";
import { LaunchStudio } from "../launch/LaunchStudio.js";
import { ErrorNote, Spinner, StatusPill } from "../components/ui.js";
import { handsOverToEditor, pipelineSummary } from "../lib/pipeline.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { VeysoMark } from "../components/VeysoMark.js";
import { useRecorderExtension } from "../lib/extension.js";
import { api, type ProjectDetail, type StatusResponse } from "../api.js";
import { dismissUpload, useUploadInFlight } from "../lib/uploadActivity.js";
import { TrashIcon } from "../components/icons.js";
import { capture } from "../lib/posthog.js";
import { ProgressLine } from "../components/ProgressLine.js";
import { emailNoteFor } from "../lib/eta.js";
import { useJobActivity } from "../lib/jobActivity.js";
import { useAccount } from "../auth/Gate.js";
import { useIsAdmin } from "../admin/useIsAdmin.js";

/**
 * A project's home: what it is, what is happening to it, and what to do next.
 *
 * Deliberately not a wizard. A step list here claimed the user was on "Record"
 * while the server was transcribing, because recording had in fact finished
 * minutes earlier and the steps were describing the artefacts rather than the
 * work. There is only ever one thing happening on this screen — waiting for a
 * recording, or waiting for the pipeline — so it shows that one thing, and
 * hands over to the editor the moment there is something to edit.
 */
export const ProjectOverview: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const account = useAccount();
  // Whether the processing job will email the person looking.
  const { active: jobs } = useJobActivity();
  const [error, setError] = useState<string | null>(null);
  // An admin can open any team's project, and the API refuses every change to
  // one that isn't theirs. Asked only for an admin: nobody else can be here
  // with another team's project.
  const admin = useIsAdmin();
  const [ownOrgId, setOwnOrgId] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (!admin) return;
    let live = true;
    void api
      .team()
      .then((team) => {
        if (live) setOwnOrgId(team.orgId);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [admin]);
  // A project with no team is judged by who made it, which the web app can't
  // see; those are the admin's own pre-team projects far more often than not.
  const readOnly =
    admin && project !== null && project.orgId !== null && ownOrgId !== undefined && project.orgId !== ownOrgId;
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  /**
   * Runs the pipeline again on the recording that is already here.
   *
   * Reloads rather than patching the status in place: the server has just put
   * the project back to `uploaded` and queued a job, and every block on this
   * page is derived from that. Guessing the new state here would be a second
   * description of the pipeline to keep in step with the first.
   */
  const retry = async (): Promise<void> => {
    if (retrying) return;
    setRetrying(true);
    setRetryError(null);
    try {
      await api.reprocess(id!);
      window.location.reload();
    } catch (cause) {
      // Left on the page rather than thrown away. The likely refusals are
      // "still no credit" and "already processing", and both are things
      // somebody needs to read.
      setRetryError((cause as Error).message);
      setRetrying(false);
    }
  };
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { available: recorderAvailable } = useRecorderExtension();

  // "Nothing more is going to happen without the user." A draft is still
  // watched: the recording arrives from the extension, not from this tab.
  //
  // A load error settles it too. A deleted project answers 404 forever, and
  // polling it every two seconds only produces an endless column of console
  // errors behind a page that already says what is wrong.
  const settled =
    Boolean(error) || status?.status === "ready" || status?.status === "completed" || status?.status === "failed";

  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteProject(id);
      capture("project_deleted", { project_id: id, deletion_surface: "project_overview" });
      navigate("/dashboard");
    } catch (cause) {
      setError((cause as Error).message);
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  // Bumped to pull the project again after something changed it — a
  // regeneration puts it back to `processing`, and the page has to notice.
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [detail, current] = await Promise.all([api.getProject(id), api.status(id)]);
        if (cancelled) return;
        setProject(detail);
        setStatus(current);
        setError(null);
      } catch (cause) {
        if (!cancelled) setError((cause as Error).message);
      }
    };

    void load();

    // A finished project will not change on its own, and this page is now the
    // one you land on and stay on, so a permanent 2-second timer against a
    // static project is pure waste.
    const watching = !settled;
    if (!watching) {
      return () => {
        cancelled = true;
      };
    }

    const timer = setInterval(() => void load(), 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [id, settled, reloads]);

  const completed = status?.completedSteps ?? [];
  // Armed for the extension but nothing uploaded yet. Showing an all-empty
  // pipeline here would read as "stuck" rather than "your turn".
  const awaitingRecording =
    search.get("awaiting") === "recording" && status?.status === "draft" && completed.length === 0;
  const ready = handsOverToEditor(status?.status, project ? project.kind : null);

  /**
   * A project with nothing in it.
   *
   * Created, then never given a recording — which used to happen by itself:
   * the project row was made before credit was checked, so an account with
   * nothing to pay with got an empty project and a refused upload. The row is
   * gated now, but the ones already made are still here, and so is the
   * ordinary way to make one: start a project, change your mind, navigate
   * away.
   *
   * It matters because the fallback panel below announced "this project is
   * draft — nothing here needs you" and offered to show the pipeline steps,
   * for a pipeline that had never run. Nothing to see, said at length.
   */
  const empty = status?.status === "draft" && completed.length === 0;

  const summary = pipelineSummary(status);
  const upload = useUploadInFlight(id);

  // Hand over as soon as there is something to edit — on arrival as well as when
  // processing finishes under the user's nose. This screen exists only for the
  // states the editor cannot represent: waiting for a recording, working on
  // one, or failing. Deleting lives on the dashboard and in the editor.
  useEffect(() => {
    // Not while a file is still going up. A project that has been through the
    // pipeline before still reads "ready" during a re-upload, and handing over
    // to the editor then would drop the user into the old cut with no sign
    // that a new one is on its way.
    if (ready && !upload) navigate(`/projects/${id}/editor`, { replace: true });
  }, [ready, upload, id, navigate]);

  return (
    <AppShell
      live={status?.status === "processing"}
      wide={project?.kind === "launch"}
      nav={project?.kind === "launch" ? <ProjectNav projectId={project.id} kind="launch" current="edit" /> : null}
      breadcrumb={
        <>
          <Link to="/dashboard" className="text-ink-400 transition-colors hover:text-ink-200">
            Projects
          </Link>
          <span className="text-ink-400">/</span>
          <span className="truncate font-medium text-ink-100">{project?.name ?? "…"}</span>
        </>
      }
      actions={
        <>
          {/* Reachable while the project is stuck or failed, which is exactly
              when the recorded history is the only account of what went wrong
              — and hidden when nothing has run, because then it is a link to
              an empty page. */}
          {empty ? null : (
            <Link to={`/projects/${id}/steps`} className="btn btn-ghost btn-sm">
              Steps
            </Link>
          )}
          <button
            className="btn-icon btn-icon-danger"
            title="Delete project"
            aria-label="Delete project"
            onClick={() => setConfirmingDelete(true)}
          >
            <TrashIcon />
          </button>
          {ready ? (
            <button className="btn btn-primary" onClick={() => navigate(`/projects/${id}/editor`)}>
              Open editor
            </button>
          ) : null}
        </>
      }
    >
      {readOnly ? (
        <div className="mb-4 rounded-[6px] border border-signal-warn/30 bg-signal-warn/10 px-3 py-2 text-[12.5px] text-signal-warn">
          Viewing as admin. Changes are refused.
        </div>
      ) : null}
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {/*
        Nothing is drawn until it is known which kind of project this is.

        The branch below keys on loaded data, so an unloaded project took the
        second arm and painted the entire recording pipeline — upload,
        transcript, script — for a launch video that has none of it. On a cold
        load that flashed a failed-looking screen before the real one arrived;
        on a warm one it did not, which is why it looked like clicking twice
        fixed it.
      */}
      {!project && !error ? (
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      ) : project?.kind === "launch" ? (
        <div className="mb-6">
          <h1 className="text-[26px] font-semibold text-ink-100">{project.name}</h1>
          <p className="mt-1 mb-5 text-[13px] text-ink-300">
            A motion graphic made from your product, not from a recording.
          </p>
          <LaunchStudio project={project} onChanged={() => setReloads((count) => count + 1)} />
        </div>
      ) : (
        <>
          <div className="mb-6 flex items-start justify-between gap-6">
            <div>
              <h1 className="text-[26px] font-semibold text-ink-100">{project?.name ?? "Loading…"}</h1>
              <div className="mt-2">{status ? <StatusPill status={status.status} /> : null}</div>
            </div>
            {project?.assets.find((asset) => asset.kind === "thumbnail") ? (
              <img
                src={`/assets/${project.assets.find((asset) => asset.kind === "thumbnail")!.key}`}
                alt=""
                className="h-[86px] w-[152px] rounded-[8px] object-cover outline outline-1 -outline-offset-1 outline-[var(--edge-strong)]"
              />
            ) : null}
          </div>

          {/* The upload, as the first step of the pipeline — because it is one. It
          is started by the new-project form and outlives it, so this is the
          first screen that can show it, and until it lands there is no job for
          the pipeline below to report. */}
          {upload ? (
            <section className="panel mb-4 p-5">
              {upload.error ? (
                <>
                  {/* A refusal is not a failure. Being turned away for want of
                  credit is an ordinary answer with an obvious next step, and
                  dressing it as a broken upload sends somebody looking for a
                  fault that is not there. */}
                  {upload.errorCode === "no_credit" ? (
                    <>
                      <h2 className="text-[13px] font-semibold text-ink-100">Add credit to continue</h2>
                      <p className="mt-2 text-[13px] leading-relaxed text-ink-300">{upload.error}</p>
                      {/* No way past it. "Not now" used to dismiss this and leave
                      somebody on a project with no recording in it, which then
                      showed pipeline steps for work that had never started and
                      a timeline that was never built — a broken-looking project
                      made by a button offered as the polite option.

                      The way out is back to the projects that already exist,
                      which is a different thing from carrying on without
                      credit. */}
                      <div className="mt-3 flex items-center gap-2">
                        <Link to="/settings#billing" className="btn btn-primary btn-sm">
                          Add credit
                        </Link>
                        <Link to="/dashboard" className="btn btn-ghost btn-sm">
                          Back to projects
                        </Link>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="text-[13px] font-semibold text-signal-bad">Upload failed</h2>
                      <p className="mt-2 text-[13px] leading-relaxed text-ink-300">{upload.error}</p>
                      <button className="btn btn-ghost btn-sm mt-3" onClick={dismissUpload}>
                        Dismiss
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-[13px] font-semibold text-ink-100">
                      Uploading <span className="text-ink-300">{upload.filename}</span>
                    </h2>
                    <span className="tnum text-[12px] text-ink-300">{Math.round(upload.progress * 100)}%</span>
                  </div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-bay-800">
                    <div
                      className="h-full rounded-full bg-scope-400 transition-[width] duration-200"
                      style={{ width: `${Math.max(2, upload.progress * 100)}%` }}
                    />
                  </div>
                  <p className="mt-3 text-[12px] leading-relaxed text-ink-400">
                    Processing starts by itself the moment this lands. Leaving this page is fine — the upload continues.
                    Reloading it is not: the file only exists in this tab.
                  </p>
                </>
              )}
            </section>
          ) : null}

          {awaitingRecording ? (
            <section className="panel mb-4 p-5">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2 w-2">
                  <span className="absolute h-2 w-2 animate-ping rounded-full bg-scope-400 opacity-70" />
                  <span className="relative h-2 w-2 rounded-full bg-scope-400" />
                </span>
                <h2 className="text-[13px] font-semibold text-ink-100">
                  {recorderAvailable ? "Waiting for your recording" : "Add the recorder to Chrome"}
                </h2>
              </div>
              {!recorderAvailable ? (
                <>
                  {/* This used to say the recorder was coming and offer only the
                  upload, because there was nowhere to install it from. There
                  is now, so the install leads and the upload stays beside it —
                  somebody who does not want an extension is still two clicks
                  from a finished video. */}
                  <p className="mt-3 text-[13px] leading-relaxed text-ink-300">
                    This browser does not have the recorder. It captures the tab and every click, which is what lets the
                    finished video zoom to the right thing on its own. Upload a recording instead and the rest of the
                    pipeline is identical.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <a className="btn btn-primary" href={CHROME_STORE_URL} target="_blank" rel="noreferrer">
                      Add to Chrome
                    </a>
                    <Link className="btn btn-secondary" to="/projects/new/demo">
                      Upload a recording
                    </Link>
                  </div>
                </>
              ) : (
                <ol className="mt-3 flex flex-col gap-2 text-[13px] leading-relaxed text-ink-300">
                  <li>
                    <span className="tnum mr-2 text-ink-400">1</span>Switch to the tab you want to record.
                  </li>
                  <li>
                    <span className="tnum mr-2 text-ink-400">2</span>Click the{" "}
                    <span className="inline-flex items-center gap-1.5 align-middle">
                      {/* A picture of the button being described, so it has to be
                      the real mark — an approximation here points at the wrong
                      thing in a crowded toolbar. */}
                      <VeysoMark size={12} />
                      <span className="font-medium text-ink-100">Veyso</span>
                    </span>{" "}
                    extension icon — this project is already selected.
                  </li>
                  <li>
                    <span className="tnum mr-2 text-ink-400">3</span>Press{" "}
                    <strong className="text-ink-100">Record this tab</strong>, do your demo, then{" "}
                    <strong className="text-ink-100">Stop and send</strong>.
                  </li>
                </ol>
              )}
              <p className="mt-3 text-[11px] leading-relaxed text-ink-400">
                Chrome only allows a tab recording to start from the extension itself, so this page cannot press it for
                you. This screen updates on its own once the recording arrives.
              </p>
            </section>
          ) : null}

          <ConfirmDialog
            open={confirmingDelete}
            title={`Delete "${project?.name ?? "this project"}"?`}
            body="This removes the recording, transcript, script, timeline and any rendered video. It cannot be undone."
            confirmLabel="Delete project"
            destructive
            busy={deleting}
            onCancel={() => (deleting ? undefined : setConfirmingDelete(false))}
            onConfirm={() => void remove()}
          />

          {summary ? (
            <section className="panel p-5">
              <ProgressLine
                id={status?.jobId ?? `process-${id}`}
                label={summary.text}
                progress={summary.progress}
                eta={status?.eta}
                note={
                  emailNoteFor(jobs, status?.jobId, account?.email ?? null, "it's ready to edit") ??
                  "This runs in the background — you can close the tab and come back."
                }
              />
            </section>
          ) : null}

          {status?.error ? (
            <div className="mb-4">
              {/* The same distinction the upload block makes above, for the
              failure the worker raises once it has measured the recording.
              Both say "add credit"; only one of them used to offer a way to.

              A red box with no button is the worst version of this: it reads
              as something broken, when what actually happened is an ordinary
              answer with an obvious next step — and the recording is safe, so
              the step is genuinely all that is left to do. */}
              <section className="panel p-5">
                <h2 className="text-[13px] font-semibold text-ink-100">
                  {isCreditRefusal(status.errorCode)
                    ? "Add credit to continue"
                    : status.errorCode === "invalid_media"
                      ? "This recording can't be used"
                      : "Processing failed"}
                </h2>
                <p className="mt-2 text-[13px] leading-relaxed text-ink-300">{status.error}</p>
                {retryError ? <p className="mt-2 text-[13px] text-signal-bad">{retryError}</p> : null}
                <div className="mt-3 flex items-center gap-2">
                  {/* Offered whatever went wrong, because the recording is here
                  either way and every failure used to be a dead end — the only
                  thing that started work was an upload.

                  It is also what answers a project that failed before the
                  failure code was recorded: the button does not need to know
                  why, only that there is a file to run again. */}
                  {/* Except for a file with nothing in it: running it again fails
                  the same way, so the way forward is a new recording. */}
                  {status.errorCode === "invalid_media" ? null : (
                    <button className="btn btn-primary btn-sm" disabled={retrying} onClick={() => void retry()}>
                      {retrying ? "Starting…" : "Try again"}
                    </button>
                  )}
                  {isCreditRefusal(status.errorCode) ? (
                    <Link to="/settings#billing" className="btn btn-secondary btn-sm">
                      Add credit
                    </Link>
                  ) : null}
                  <Link to="/dashboard" className="btn btn-ghost btn-sm">
                    Back to projects
                  </Link>
                </div>
              </section>
            </div>
          ) : null}

          {/* Whatever is left. Every block above answers one state, and a state none
          of them answers used to render an empty page under a status pill —
          which is how a project that was merely rendering became one you could
          not open. This says what is known and points at the record, which is
          more use than nothing whatever the state turns out to be. */}
          {!upload && !awaitingRecording && !summary && !ready && !status?.error ? (
            <section className="panel p-5">
              {empty ? (
                /* Say what is true: there is no recording in this project. The
               panel used to offer the pipeline steps here, which for a project
               that never started is a page of nothing. */
                <>
                  <h2 className="text-[13px] font-semibold text-ink-100">{"This project has no recording yet"}</h2>
                  <p className="mt-2 text-[13px] leading-relaxed text-ink-300">
                    {
                      "Nothing has been uploaded to it, so there is nothing to edit or export. Start again with a recording, or delete it."
                    }
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <Link to="/projects/new/demo" className="btn btn-primary btn-sm">
                      {"Start a project"}
                    </Link>
                    <button className="btn btn-ghost btn-sm" onClick={() => setConfirmingDelete(true)}>
                      {"Delete this one"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[13px] leading-relaxed text-ink-300">
                    This project is {status?.status ?? "loading"}
                    {status?.step ? ` — ${status.step}` : ""}. Nothing here needs you.
                  </p>
                  <Link to={`/projects/${id}/steps`} className="btn btn-ghost btn-sm mt-3">
                    See what has run
                  </Link>
                </>
              )}
            </section>
          ) : null}
        </>
      )}
    </AppShell>
  );
};
